import React, { useEffect, useRef, useState } from "react";
import {
  addDoc,
  collection,
  getDocs,
  getFirestore,
  query,
  where,
  Timestamp,
} from "firebase/firestore";

// ---------- config
const COLLECTION = "stocktake_scanned";
const LS_KEY = "pt_stocktake_local_v1";

// ---------- utils
const parseItemBarcode = (raw) => {
  const s = (raw || "").replace(/\D/g, "");
  if (s.length < 9) return { ok: false, error: "Barcode too short" };
  const SerialNumber = s.slice(0, 6);
  const Special = s.slice(6, 8);
  const ProductId = s.slice(8);
  return { ok: true, SerialNumber, Special, ProductId };
};

const loadLocal = () => {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || "[]");
  } catch {
    return [];
  }
};
const saveLocal = (arr) => localStorage.setItem(LS_KEY, JSON.stringify(arr));

const makeLocalRow = ({ SerialNumber, Special, ProductId, LocationId }) => ({
  // local-only metadata
  localId: `${SerialNumber}-${Special}-${ProductId}-${LocationId}-${Date.now()}`,
  uploaded: false,
  verified: false,
  // exact Firestore fields below
  SerialNumber,
  Special,
  ProductId,
  LocationId,
  ScannedTimeISO: new Date().toISOString(), // local-only convenience for display
});

// ---------- component
const StockTakeForm = () => {
  const db = getFirestore();

  const [itemBarcode, setItemBarcode] = useState("");
  const [locationBarcode, setLocationBarcode] = useState("");
  const [localQueue, setLocalQueue] = useState(loadLocal()); // local backup
  const [statusMsg, setStatusMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const itemRef = useRef(null);
  const locRef = useRef(null);

  useEffect(() => {
    // focus on item input initially
    itemRef.current?.focus();
  }, []);

  const resetInputs = () => {
    setItemBarcode("");
    setLocationBarcode("");
    itemRef.current?.focus();
  };

  const uploadOne = async (row) => {
    // Upload EXACT fields only, as requested
    const payload = {
      SerialNumber: row.SerialNumber,
      Special: row.Special,
      ProductId: row.ProductId,
      LocationId: row.LocationId,
      ScannedTime: Timestamp.now(),
    };
    await addDoc(collection(db, COLLECTION), payload);
  };

  const handleSubmit = async () => {
    if (!itemBarcode || !locationBarcode) return;

    // Parse item barcode
    const parsed = parseItemBarcode(itemBarcode);
    if (!parsed.ok) {
      setStatusMsg(parsed.error || "Invalid item barcode");
      return;
    }

    const LocationId = locationBarcode.trim();
    const { SerialNumber, Special, ProductId } = parsed;

    // Build local row and persist immediately
    const localRow = makeLocalRow({ SerialNumber, Special, ProductId, LocationId });
    const next = [localRow, ...localQueue];
    setLocalQueue(next);
    saveLocal(next);

    // Upload now (best effort)
    setBusy(true);
    try {
      await uploadOne(localRow);

      // mark as uploaded locally
      const updated = next.map((r) =>
        r.localId === localRow.localId ? { ...r, uploaded: true } : r
      );
      setLocalQueue(updated);
      saveLocal(updated);
      setStatusMsg("Saved locally + uploaded.");
    } catch (e) {
      // keep local (uploaded=false), user can Sync later
      setStatusMsg("Saved locally. Upload failed—will need Sync.");
    } finally {
      setBusy(false);
      resetInputs();
    }
  };

  // Compare local rows to Firestore by exact equality of the 4 fields
  // (ignoring ScannedTime). If not found in Firestore, re-upload.
  const syncAndVerify = async () => {
    setBusy(true);
    setStatusMsg("Syncing…");
    try {
      const updated = [...localQueue];

      for (let i = 0; i < updated.length; i++) {
        const r = updated[i];

        // Query Firestore: all four equalities
        const qref = query(
          collection(db, COLLECTION),
          where("SerialNumber", "==", r.SerialNumber),
          where("Special", "==", r.Special),
          where("ProductId", "==", r.ProductId),
          where("LocationId", "==", r.LocationId)
        );

        const snap = await getDocs(qref);
        if (snap.empty) {
          // not there — upload now
          try {
            await uploadOne(r);
            updated[i] = { ...r, uploaded: true, verified: true };
          } catch {
            updated[i] = { ...r, verified: false }; // leave as is; user can retry
          }
        } else {
          // exists in Firestore — mark verified
          updated[i] = { ...r, uploaded: true, verified: true };
        }
      }

      setLocalQueue(updated);
      saveLocal(updated);
      setStatusMsg("Sync complete.");
    } catch (e) {
      setStatusMsg("Sync failed. Check network and try again.");
    } finally {
      setBusy(false);
    }
  };

  const clearLocal = () => {
    if (!window.confirm("This will clear the local backup list (Firestore stays as-is). Continue?")) return;
    setLocalQueue([]);
    saveLocal([]);
    setStatusMsg("Local list cleared.");
  };

  return (
    <div className="p-4 max-w-lg mx-auto">
      <h1 className="text-2xl font-semibold text-center mb-4">📦 Stocktake Capture</h1>

      {/* Inputs: big, scanner-friendly */}
      <div className="flex flex-col gap-3 mb-4">
        <input
          ref={itemRef}
          type="text"
          inputMode="numeric"
          placeholder="Scan ITEM barcode"
          className="p-4 text-lg border rounded-md focus:ring-2 focus:ring-blue-500"
          value={itemBarcode}
          onChange={(e) => setItemBarcode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              // after item, go to location
              locRef.current?.focus();
            }
          }}
        />
        <input
          ref={locRef}
          type="text"
          placeholder="Scan LOCATION barcode"
          className="p-4 text-lg border rounded-md focus:ring-2 focus:ring-blue-500"
          value={locationBarcode}
          onChange={(e) => setLocationBarcode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && itemBarcode) {
              handleSubmit();
            }
          }}
        />
        <button
          disabled={!itemBarcode || !locationBarcode || busy}
          onClick={handleSubmit}
          className={`w-full py-3 rounded-md text-white ${!itemBarcode || !locationBarcode || busy ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700"}`}
        >
          Save & Upload
        </button>
      </div>

      {/* Actions */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={syncAndVerify}
          disabled={busy}
          className="flex-1 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          Sync & Verify
        </button>
        <button
          onClick={clearLocal}
          disabled={busy}
          className="px-3 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white"
        >
          Clear Local
        </button>
      </div>

      {/* Status */}
      {statusMsg && (
        <div className="mb-3 text-sm text-gray-700">{statusMsg}</div>
      )}

      {/* Local backup list */}
      <div className="text-sm">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-medium">Local backup ({localQueue.length})</h2>
          <span className="text-xs text-gray-500">
            {localQueue.filter((r) => r.uploaded).length} uploaded •{" "}
            {localQueue.filter((r) => r.verified).length} verified
          </span>
        </div>
        <div className="border rounded-md overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-2 py-1 text-left">Serial</th>
                <th className="px-2 py-1 text-left">Spec</th>
                <th className="px-2 py-1 text-left">Prod</th>
                <th className="px-2 py-1 text-left">Loc</th>
                <th className="px-2 py-1 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {localQueue.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-3 text-center text-gray-500">
                    Nothing scanned yet.
                  </td>
                </tr>
              ) : (
                localQueue.map((r) => (
                  <tr key={r.localId} className="border-t">
                    <td className="px-2 py-1">{r.SerialNumber}</td>
                    <td className="px-2 py-1">{r.Special}</td>
                    <td className="px-2 py-1">{r.ProductId}</td>
                    <td className="px-2 py-1">{r.LocationId}</td>
                    <td className="px-2 py-1">
                      {r.verified
                        ? "verified"
                        : r.uploaded
                        ? "uploaded"
                        : "local only"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Firestore fields: <code>SerialNumber</code>, <code>Special</code>, <code>ProductId</code>, <code>LocationId</code>, <code>ScannedTime</code>.
        </p>
      </div>
    </div>
  );
};

export default StockTakeForm;
